import React, { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { IncidentsProvider } from "@/contexts/IncidentsContext";
import { Toaster } from "@/components/ui/sonner";
import BootScreen from "@/components/BootScreen";
import LoginPage from "@/pages/LoginPage";
import AppShell from "@/components/layout/AppShell";
import CadDashboard from "@/pages/CadDashboard";
import WeatherPage from "@/pages/WeatherPage";
import CamerasPage from "@/pages/CamerasPage";
import ActiveIncidentsPage from "@/pages/ActiveIncidentsPage";
import SettingsPage from "@/pages/SettingsPage";
import ATCPage from "@/pages/ATCPage";
import NYCTPage from "@/pages/NYCTPage";
import HistoryPage from "@/pages/HistoryPage";
import ChatPage from "@/pages/ChatPage";
import AdminPortalPage from "@/pages/AdminPortalPage";
import EmsAnalyticsPage from "@/pages/EmsAnalyticsPage";
import { SettingsProvider } from "@/contexts/SettingsContext";
import ForcePasswordChangeDialog from "@/components/auth/ForcePasswordChangeDialog";

function ProtectedShell() {
  const { user } = useAuth();
  // Skip the boot animation when the page is opened in embed mode (new tab) or once we've already
  // played it for this browser session. Persisting "cad_booted" in sessionStorage means the
  // animation only fires on the very first login of the session.
  const isEmbed = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("embed") === "1";
  const alreadyBooted = typeof window !== "undefined" && window.sessionStorage.getItem("cad_booted") === "1";
  const [booted, setBooted] = useState(isEmbed || alreadyBooted);

  if (user === null) {
    // Session check in progress — quick spinner, not the full boot
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background text-muted-foreground font-mono text-xs tracking-widest">
        VERIFYING SESSION…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Role gating — free users can't reach ops pages. Server enforces this too;
  // we mirror it here so navigation is consistent.
  const OPS = new Set(["dispatcher", "admin_dispatcher", "subscriber", "supervisor", "admin"]);
  const ADMIN = new Set(["admin_dispatcher", "admin"]);
  const isOps = OPS.has(user.role);
  const isAdmin = ADMIN.has(user.role);
  if (!booted) {
    return <BootScreen onComplete={() => {
      try { window.sessionStorage.setItem("cad_booted", "1"); } catch (_e) { /* ignore */ }
      setBooted(true);
    }} />;
  }
  return (
    <IncidentsProvider>
      <SettingsProvider>
        <ForcePasswordChangeDialog />
        <AppShell>
        <Routes>
          <Route path="/" element={isOps ? <CadDashboard /> : <Navigate to="/cameras" replace />} />
          <Route path="/active-incidents" element={isOps ? <ActiveIncidentsPage /> : <Navigate to="/cameras" replace />} />
          <Route path="/weather" element={<WeatherPage />} />
          <Route path="/cameras" element={<CamerasPage />} />
          <Route path="/atc" element={<ATCPage />} />
          <Route path="/nyct" element={<NYCTPage />} />
          <Route path="/ems-analytics" element={<EmsAnalyticsPage />} />
          <Route path="/history" element={isOps ? <HistoryPage /> : <Navigate to="/cameras" replace />} />
          <Route path="/chat" element={isOps ? <ChatPage /> : <Navigate to="/cameras" replace />} />
          <Route path="/admin" element={isAdmin ? <AdminPortalPage /> : <Navigate to="/cameras" replace />} />
          <Route path="/settings" element={isAdmin ? <SettingsPage /> : <Navigate to="/cameras" replace />} />
          <Route path="*" element={<Navigate to={isOps ? "/" : "/cameras"} replace />} />
        </Routes>
      </AppShell>
      </SettingsProvider>
    </IncidentsProvider>
  );
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/*" element={<ProtectedShell />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster richColors position="top-right" closeButton />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
